import { Box, Divider } from '@mui/material';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import Typography, {
  TypographyVariant,
  useGetTypographyStyles,
} from '../../../component_library/components/Typography';
import HtmlBox from '../../../component_library/components/HtmlBox';
import type { ILoginInfoData } from '../../../dotcms/dotcms-models/dotcms-login-info';
import type { ITheme } from '../../../component_library/styles/theme';
import type { HtmlFromDotcms } from '../../../dotcms/dotcms-models/converters-from-dotcms';
import {
  ButtonVariant,
  LinkButton,
} from '../../../component_library/components/Button';
import { DataElementId } from '../../../constants/data-element-tracking-ids';
import { AccordionLight } from '../../../component_library/components/Accordion/AccordionLight';
import { useThemeToken } from '../../../hooks/use-get-theme/use-get-theme';

interface IProps {
  data: ILoginInfoData;
  theme: ITheme;
  isMobile: boolean;
  isTabletMini: boolean;
  returnUrl?: string;
}

export const LoginPageInfo: React.FC<IProps> = ({
  data,
  theme,
  isMobile,
  isTabletMini,
  returnUrl,
}) => {
  const themeToken = useThemeToken();
  const htmlWithLinkStyles = {
    a: useGetTypographyStyles(
      TypographyVariant.LINKS_MEDIUM,
      theme,
      themeToken,
      theme.colors.secondary,
    ),
  };

  const renderNoteWithIcon = (icon: IconProp, note: HtmlFromDotcms) => (
    <Box sx={{ display: 'flex', flexDirection: 'row', columnGap: '8px' }}>
      <FontAwesomeIcon icon={icon} style={{ paddingTop: '4px' }} />
      <Typography variant={TypographyVariant.MEDIUM_BODY_REGULAR}>
        <HtmlBox html={note} additionalStyles={htmlWithLinkStyles} />
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '48px' }}>
      {!isMobile && !isTabletMini && (
        <Typography variant={TypographyVariant.H2}>
          <HtmlBox html={data.headline} />
        </Typography>
      )}
      {!!returnUrl && (
        <LinkButton
          to={returnUrl}
          dataElementId={DataElementId.LOGIN_PAGE_RETURN_TO_MAIN_BUTTON}
          themeToken={themeToken}
          variant={ButtonVariant.SECONDARY}
          additionalStyles={{
            justifyContent: 'center',
            width: isTabletMini || isMobile ? undefined : 'max-content',
          }}
        >
          <HtmlBox html={data.returnButtonText} />
        </LinkButton>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '24px' }}>
        {renderNoteWithIcon(faBell, data.alternateEmailNote)}
        {renderNoteWithIcon(faQuestionCircle, data.contactUsNote)}
        {data.eligibilityRequirementsToggle && (
          <AccordionLight
            dataElementId={DataElementId.LOGIN_PAGE_REQUIREMENTS_ACCORDION}
            title={<HtmlBox html={data.eligibilityRequirementsTitle} />}
            details={<HtmlBox html={data.eligibilityRequirementsNote} />}
            theme={theme}
            isWithIconSpace={isMobile}
          />
        )}
        <Divider
          sx={{ border: `1px solid ${theme.colors.grayscale.outlineGrey}` }}
        />
        <Box>
          <Typography variant={TypographyVariant.MEDIUM_BODY_BOLD}>
            <HtmlBox html={data.formerEmployeesTitle} />
          </Typography>

          <Typography variant={TypographyVariant.MEDIUM_BODY_REGULAR}>
            <HtmlBox
              html={
                returnUrl
                  ? data.formerEmployeesReturnNote
                  : data.formerEmployeesNote
              }
              additionalStyles={htmlWithLinkStyles}
            />
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
